import React, { useState, useEffect, useRef, useContext } from "react";
import { Container, Row, Col } from "react-bootstrap";
import "../../../assets/css/gcadmin/manageblogs/ManageBlogsPage.css";
import axios from "axios";
import Swal from "sweetalert2";
import AppContext from "../../../context/AppContext";
import Pagination from "../../../components/general/pagination/Pagination";
import Loader from "../../../components/general/loader/Loader";

const ITEMS_PER_PAGE = 9; // ✅ 3x3 grid per page (lg pe 3 per row)

/* ══════════════════════════════════════════
   Rich Text Toolbar Button
══════════════════════════════════════════ */
const ToolbarBtn = ({ onClick, title, children }) => (
  <button
    type="button"
    className="gcAdmin-blog-toolbar-btn"
    onClick={onClick}
    title={title}
  >
    {children}
  </button>
);

/* ══════════════════════════════════════════
   Blog Form Modal
══════════════════════════════════════════ */
const BlogFormModal = ({ onClose, editData = null, onSaved, baseUrl, authToken }) => {
  const [visible, setVisible] = useState(false);
  const [form, setForm] = useState({
    title:    editData?.title    || "",
    author:   editData?.author   || "",
    category: editData?.category || "Education",
    excerpt:  editData?.excerpt  || "",
    content:  editData?.content  || "",
    tags:     editData?.tags     || [],
  });
  const [imgFile,   setImgFile]   = useState(null);     // ✅ file object
  const [imgPreview,setImgPreview]= useState(          // ✅ preview URL
    editData?.img
      ? editData.img.startsWith("http")
        ? editData.img
        : `http://127.0.0.1:8000/storage/${editData.img}`
      : null
  );
  const [tagInput,  setTagInput]  = useState("");
  const [errors,    setErrors]    = useState({});
  const [loading,   setLoading]   = useState(false);
  const editorRef                 = useRef(null);
  const tagInputRef               = useRef(null);
  const fileInputRef              = useRef(null);

  const CATEGORIES = ["Education", "Technology", "Books", "Research", "General"];

  useEffect(() => { setTimeout(() => setVisible(true), 20); }, []);

  // ✅ Edit mode mein editor content set karo
  useEffect(() => {
    if (editData?.content && editorRef.current) {
      editorRef.current.innerHTML = editData.content;
    }
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300);
  };

  const set = (key, val) => {
    setForm((p) => ({ ...p, [key]: val }));
    if (errors[key]) setErrors((p) => ({ ...p, [key]: "" }));
  };

  /* ── Image file select ── */
  const handleImgChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImgFile(file);
    setImgPreview(URL.createObjectURL(file)); // local preview
  };

  /* ── Tags ── */
  const addTag = () => {
    const t = tagInput.trim();
    if (t && !form.tags.includes(t)) set("tags", [...form.tags, t]);
    setTagInput("");
    tagInputRef.current?.focus();
  };
  const removeTag = (t) => set("tags", form.tags.filter((x) => x !== t));
  const onTagKeyDown = (e) => {
    if (e.key === "Enter")     { e.preventDefault(); addTag(); }
    if (e.key === "Backspace" && !tagInput && form.tags.length) {
      removeTag(form.tags[form.tags.length - 1]);
    }
  };

  /* ── Rich text ── */
  const exec = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
  };

  /* ── Validate ── */
  const validate = () => {
    const e = {};
    if (!form.title.trim())   e.title   = "Title is required";
    if (!form.author.trim())  e.author  = "Author is required";
    if (!form.excerpt.trim()) e.excerpt = "Excerpt is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ── Submit ── */
  const handleSubmit = () => {
    if (!validate()) return;
    setLoading(true);

    const content = editorRef.current?.innerHTML || "";

    const formData = new FormData();
    formData.append("title",    form.title);
    formData.append("author",   form.author);
    formData.append("category", form.category);
    formData.append("excerpt",  form.excerpt);
    formData.append("content",  content);
    formData.append("tags",     JSON.stringify(form.tags));
    if (imgFile) {
      formData.append("img", imgFile); // ✅ file append
    }

    const url = editData
      ? `${baseUrl}/admin/blogs/update/${editData.id}`
      : `${baseUrl}/admin/blogs/addBlog`;

    axios
      .post(url, formData, {
        headers: { "Authorization": `Bearer ${authToken}` },
      })
      .then((res) => {
        setLoading(false);
        if (res.data.success) {
          Swal.fire({
            icon: "success",
            title: editData ? "Blog Updated!" : "Blog Published!",
            timer: 1500,
            showConfirmButton: false,
          });
          onSaved();   // list refresh karo
          handleClose();
        } else {
          Swal.fire({ icon: "error", title: "Failed", text: res.data.message });
        }
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
        Swal.fire({ icon: "error", title: "Error", text: "Something went wrong." });
      });
  };

  return (
    <div
      className={`gcAdmin-blog-backdrop ${visible ? "gcAdmin-blog-backdrop--in" : ""}`}
      onClick={handleClose}
    >
      <div
        className={`gcAdmin-blog-modal ${visible ? "gcAdmin-blog-modal--in" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="gcAdmin-blog-modal-header">
          <h5 className="gcAdmin-blog-modal-title">
            {editData ? "Edit Blog Post" : "New Blog Post"}
          </h5>
          <button className="gcAdmin-blog-modal-close" onClick={handleClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="gcAdmin-blog-modal-body">

          {/* Title */}
          <div className="gcAdmin-blog-field">
            <label className="gcAdmin-blog-label">Title</label>
            <input
              className={`gcAdmin-blog-input ${errors.title ? "gcAdmin-blog-input--error" : ""}`}
              type="text"
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder="Enter blog title..."
              autoFocus
            />
            {errors.title && <span className="gcAdmin-blog-error">{errors.title}</span>}
          </div>

          {/* Author + Category */}
          <Row className="g-3">
            <Col xs={12} sm={6}>
              <div className="gcAdmin-blog-field">
                <label className="gcAdmin-blog-label">Author</label>
                <input
                  className={`gcAdmin-blog-input ${errors.author ? "gcAdmin-blog-input--error" : ""}`}
                  type="text"
                  value={form.author}
                  onChange={(e) => set("author", e.target.value)}
                  placeholder="Author name"
                />
                {errors.author && <span className="gcAdmin-blog-error">{errors.author}</span>}
              </div>
            </Col>
            <Col xs={12} sm={6}>
              <div className="gcAdmin-blog-field">
                <label className="gcAdmin-blog-label">Category</label>
                <div className="gcAdmin-blog-select-wrap">
                  <select
                    className="gcAdmin-blog-select"
                    value={form.category}
                    onChange={(e) => set("category", e.target.value)}
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  <span className="gcAdmin-blog-select-arrow">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </span>
                </div>
              </div>
            </Col>
          </Row>

          {/* ✅ Cover Image — File Upload */}
          <div className="gcAdmin-blog-field">
            <label className="gcAdmin-blog-label">Cover Image</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleImgChange}
            />
            <button
              type="button"
              className="gcAdmin-blog-upload-btn"
              onClick={() => fileInputRef.current?.click()}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                <polyline points="17 8 12 3 7 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                <line x1="12" y1="3" x2="12" y2="15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
              {imgFile ? imgFile.name : "Choose Image"}
            </button>
            {imgPreview && (
              <div className="gcAdmin-blog-img-preview">
                <img src={imgPreview} alt="Preview" />
              </div>
            )}
          </div>

          {/* Excerpt */}
          <div className="gcAdmin-blog-field">
            <label className="gcAdmin-blog-label">Excerpt</label>
            <textarea
              className={`gcAdmin-blog-textarea ${errors.excerpt ? "gcAdmin-blog-input--error" : ""}`}
              rows={3}
              value={form.excerpt}
              onChange={(e) => set("excerpt", e.target.value)}
              placeholder="Short description..."
            />
            {errors.excerpt && <span className="gcAdmin-blog-error">{errors.excerpt}</span>}
          </div>

          {/* Tags */}
          <div className="gcAdmin-blog-field">
            <label className="gcAdmin-blog-label">Keywords / Tags</label>
            <div className="gcAdmin-blog-tags-wrap">
              <div className="gcAdmin-blog-tags-input-row">
                <input
                  ref={tagInputRef}
                  className="gcAdmin-blog-tags-input"
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={onTagKeyDown}
                  placeholder="Type and press Enter"
                />
                <button type="button" className="gcAdmin-blog-tag-add-btn" onClick={addTag}>Add</button>
              </div>
              {form.tags.length > 0 && (
                <div className="gcAdmin-blog-tags-list">
                  {form.tags.map((t) => (
                    <span key={t} className="gcAdmin-blog-tag">
                      {t}
                      <button type="button" className="gcAdmin-blog-tag-remove" onClick={() => removeTag(t)}>×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Rich Text Editor */}
          <div className="gcAdmin-blog-field">
            <label className="gcAdmin-blog-label">Content</label>
            <div className="gcAdmin-blog-editor-wrap">
              <div className="gcAdmin-blog-toolbar">
                <ToolbarBtn onClick={() => exec("bold")} title="Bold"><strong>B</strong></ToolbarBtn>
                <ToolbarBtn onClick={() => exec("italic")} title="Italic"><em>I</em></ToolbarBtn>
                <ToolbarBtn onClick={() => exec("underline")} title="Underline"><u>U</u></ToolbarBtn>
                <div className="gcAdmin-blog-toolbar-divider" />
                <ToolbarBtn onClick={() => exec("formatBlock", "h2")} title="Heading">H₂</ToolbarBtn>
                <ToolbarBtn onClick={() => exec("formatBlock", "blockquote")} title="Quote">❝</ToolbarBtn>
                <div className="gcAdmin-blog-toolbar-divider" />
                <ToolbarBtn onClick={() => exec("insertUnorderedList")} title="Bullets">≡</ToolbarBtn>
                <ToolbarBtn onClick={() => exec("insertOrderedList")} title="Numbered">1≡</ToolbarBtn>
                <div className="gcAdmin-blog-toolbar-divider" />
                <ToolbarBtn onClick={() => { const u = prompt("URL:"); if (u) exec("createLink", u); }} title="Link">🔗</ToolbarBtn>
                <div className="gcAdmin-blog-toolbar-divider" />
                <ToolbarBtn onClick={() => exec("undo")} title="Undo">↩</ToolbarBtn>
                <ToolbarBtn onClick={() => exec("redo")} title="Redo">↪</ToolbarBtn>
              </div>
              <div
                ref={editorRef}
                className="gcAdmin-blog-editor"
                contentEditable
                suppressContentEditableWarning
                data-placeholder="Write your blog content..."
              />
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="gcAdmin-blog-modal-footer">
          <button className="gcAdmin-blog-cancel-btn" onClick={handleClose} disabled={loading}>
            Cancel
          </button>
          <button className="gcAdmin-blog-publish-btn" onClick={handleSubmit} disabled={loading}>
            {loading
              ? <span className="gcAdmin-res-btn-spinner" />
              : editData ? "Update" : "Publish"
            }
          </button>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════
   Delete Modal
══════════════════════════════════════════ */
const DeleteModal = ({ blog, onClose, onConfirm }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => { setTimeout(() => setVisible(true), 20); }, []);
  const handleClose = () => { setVisible(false); setTimeout(onClose, 280); };
  return (
    <div className={`gcAdmin-blog-backdrop ${visible ? "gcAdmin-blog-backdrop--in" : ""}`} onClick={handleClose}>
      <div className={`gcAdmin-blog-modal gcAdmin-blog-modal--sm ${visible ? "gcAdmin-blog-modal--in" : ""}`} onClick={(e) => e.stopPropagation()}>
        <div className="gcAdmin-blog-del-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
            <polyline points="3 6 5 6 21 6" stroke="#ef4444" strokeWidth="1.8" strokeLinecap="round"/>
            <path d="M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2" stroke="#ef4444" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </div>
        <h5 className="gcAdmin-blog-del-title">Delete Blog Post?</h5>
        <p className="gcAdmin-blog-del-msg"><strong>"{blog.title}"</strong> will be permanently deleted.</p>
        <div className="gcAdmin-blog-del-actions">
          <button className="gcAdmin-blog-cancel-btn" onClick={handleClose}>Cancel</button>
          <button className="gcAdmin-blog-del-confirm-btn" onClick={() => { onConfirm(blog.id); handleClose(); }}>Delete</button>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════
   Blog Card
══════════════════════════════════════════ */
const BlogCard = ({ blog, idx, onEdit, onDelete }) => {
  const imgUrl = blog.img
    ? blog.img.startsWith("http")
      ? blog.img
      : `http://127.0.0.1:8000/storage/${blog.img}`
    : "https://via.placeholder.com/600x300?text=No+Image";

  return (
    <div className="gcAdmin-blog-card" style={{ animationDelay: `${idx * 0.07}s` }}>
      <div className="gcAdmin-blog-card-img-wrap">
        <img
          src={imgUrl}
          alt={blog.title}
          className="gcAdmin-blog-card-img"
          onError={(e) => { e.target.style.display = "none"; }}
        />
      </div>
      <div className="gcAdmin-blog-card-body">
        <span className="gcAdmin-blog-card-category">{blog.category}</span>
        <h3 className="gcAdmin-blog-card-title">{blog.title}</h3>
        <p className="gcAdmin-blog-card-excerpt">{blog.excerpt}</p>
      </div>
      <div className="gcAdmin-blog-card-footer">
        <span className="gcAdmin-blog-card-author">{blog.author}</span>
        <div className="gcAdmin-blog-card-actions">
          <button className="gcAdmin-blog-card-btn gcAdmin-blog-card-btn--edit" onClick={() => onEdit(blog)} title="Edit">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </button>
          <button className="gcAdmin-blog-card-btn gcAdmin-blog-card-btn--delete" onClick={() => onDelete(blog)} title="Delete">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              <path d="M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════
   Main AdminBlogs Page
══════════════════════════════════════════ */
const AdminBlogs = () => {
  const [blogs,       setBlogs]       = useState([]);
  const [search,      setSearch]      = useState("");
  const [showForm,    setShowForm]    = useState(false);
  const [editBlog,    setEditBlog]    = useState(null);
  const [deleteBlog,  setDeleteBlog]  = useState(null);
  const [pageVisible, setPageVisible] = useState(false);
  const [fetching,    setFetching]    = useState(true);
  const [currentPage, setCurrentPage] = useState(1); // ✅ pagination state

  // ✅ Pagination change hone par sirf current page (max 9) cards ki
  //    chhoti loading dikhane ke liye — pura grid dobara load nahi hota
  const [viewLoading, setViewLoading] = useState(false);
  const isFirstRender = useRef(true);

  const { baseUrl, authToken } = useContext(AppContext);

  useEffect(() => { setTimeout(() => setPageVisible(true), 60); }, []);

  /* ── Fetch ── */
  const fetchBlogs = () => {
    setFetching(true);
    axios
      .get(`${baseUrl}/admin/blogs/fetchAllBlogs`, {
        headers: { "Authorization": `Bearer ${authToken}` },
      })
      .then((res) => {
        setFetching(false);
        if (res.data.success) setBlogs(res.data.blogs);
      })
      .catch((err) => {
        setFetching(false);
        console.log(err);
      });
  };

  useEffect(() => { fetchBlogs(); }, []);

  /* ── Delete ── */
  const confirmDelete = (id) => {
    axios
      .delete(`${baseUrl}/admin/blogs/delete/${id}`, {
        headers: { "Authorization": `Bearer ${authToken}` },
      })
      .then((res) => {
        if (res.data.success) {
          setBlogs((prev) => prev.filter((b) => b.id !== id));
          Swal.fire({ icon: "success", title: "Deleted!", timer: 1400, showConfirmButton: false });
        }
      })
      .catch((err) => {
        console.log(err);
        Swal.fire({ icon: "error", title: "Error", text: "Could not delete." });
      });
  };

  const filtered = blogs.filter(
    (b) =>
      b.title.toLowerCase().includes(search.toLowerCase()) ||
      b.author.toLowerCase().includes(search.toLowerCase()) ||
      b.category.toLowerCase().includes(search.toLowerCase())
  );

  /* ── Paginated slice (jo grid ko jayega) ── */
  const paginatedBlogs = filtered.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  /* ── Search change hone par page 1 pe reset ── */
  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line
  }, [search]);

  /* ── ✅ Page change hone par chhota loader — sirf current page ke
     (max 9) cards ke liye. Search change hote hi currentPage bhi 1 pe
     reset hota hai (upar wale effect se), is liye search ko alag se
     dependency mein dalne ki zaroorat nahi — nahi tu har keystroke
     par loader flicker karega. ── */
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setViewLoading(true);
    const timer = setTimeout(() => setViewLoading(false), 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [currentPage]);

  /* ── ✅ Initial fetch loading + pagination ki chhoti loading dono combine ── */
  const showLoader = fetching || viewLoading;

  return (
    <div className={`gcAdmin-blog-page ${pageVisible ? "gcAdmin-blog-page--visible" : ""}`}>
      <Container fluid className="gcAdmin-blog-container">

        {/* Header */}
        <div className="gcAdmin-blog-header">
          <div>
            <h2 className="gcAdmin-blog-title">Manage Blogs</h2>
            <p className="gcAdmin-blog-subtitle">Create, edit, and publish blog articles.</p>
          </div>
          <button
            className="gcAdmin-blog-new-btn"
            onClick={() => { setEditBlog(null); setShowForm(true); }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"/>
            </svg>
            New Blog
          </button>
        </div>

        {/* Search */}
        <div className="gcAdmin-blog-search-wrap">
          <svg className="gcAdmin-blog-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.8"/>
            <path d="M16.5 16.5L21 21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
          <input
            className="gcAdmin-blog-search"
            type="text"
            placeholder="Search blogs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Grid + Pagination — ✅ ab showLoader use ho raha hai */}
        {showLoader ? (
          <Loader
            message={fetching
              ? "Blogs loading, please wait..."
              : "Blogs loading, please wait..."}
          />
        ) : (
          <>
            <Row className="g-4 gcAdmin-blog-grid">
              {paginatedBlogs.map((blog, idx) => (
                <Col key={blog.id} xs={12} sm={6} lg={4}>
                  <BlogCard
                    blog={blog}
                    idx={idx}
                    onEdit={(b) => { setEditBlog(b); setShowForm(true); }}
                    onDelete={setDeleteBlog}
                  />
                </Col>
              ))}
              {filtered.length === 0 && (
                <Col xs={12}>
                  <div className="gcAdmin-blog-empty">
                    <svg width="44" height="44" viewBox="0 0 24 24" fill="none">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                      <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                    </svg>
                    <p>No blogs found.</p>
                  </div>
                </Col>
              )}
            </Row>

            <Pagination
              currentPage={currentPage}
              totalItems={filtered.length}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={setCurrentPage}
            />
          </>
        )}

      </Container>

      {/* Modals */}
      {showForm && (
        <BlogFormModal
          editData={editBlog}
          baseUrl={baseUrl}
          authToken={authToken}
          onSaved={fetchBlogs}
          onClose={() => { setShowForm(false); setEditBlog(null); }}
        />
      )}
      {deleteBlog && (
        <DeleteModal
          blog={deleteBlog}
          onClose={() => setDeleteBlog(null)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
};

export default AdminBlogs;